/**
 * Leviathan Web - Price Book Detail Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { GeometricPanel } from '@components/ui/GeometricPanel';
import { API_URL } from '@/config/api';

interface PriceBookEntry {
  id: string;
  productId: string;
  product: {
    id: string;
    sku: string;
    name: string;
    costPerUnit: number;
    pricePerUnit: number;
    unit: string;
    category: string;
  };
  fixedPrice: number | null;
  rackPlusAmount: number | null;
  marginPercent: number | null;
  costPlusAmount: number | null;
  minPrice: number | null;
  maxPrice: number | null;
  isActive: boolean;
}

interface Customer {
  id: string;
  customerNumber: string;
  name: string;
  type: string;
  status: string;
}

interface PriceBook {
  id: string;
  name: string;
  description: string | null;
  pricingMethod: string;
  isDefault: boolean;
  isActive: boolean;
  effectiveDate: string | null;
  expirationDate: string | null;
  prices: PriceBookEntry[];
  customers: Customer[];
  _count: {
    prices: number;
    customers: number;
  };
  createdAt: string;
  updatedAt: string;
}

const PRICING_METHOD_LABELS: Record<string, string> = {
  FIXED: 'Fixed Price',
  RACK_PLUS: 'Rack + Amount',
  MARGIN_PERCENT: 'Margin %',
  COST_PLUS: 'Cost + Amount',
};

const PriceBookDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [priceBook, setPriceBook] = useState<PriceBook | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'prices' | 'customers'>('prices');

  useEffect(() => {
    fetchPriceBook();
  }, [id]);

  const fetchPriceBook = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/pricebooks/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPriceBook(data.priceBook);
      } else {
        setError('Price book not found');
      }
    } catch (error) {
      setError('Failed to load price book');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null) return '-';
    return `$${Number(amount).toFixed(4)}`;
  };

  const getPriceDisplay = (entry: PriceBookEntry) => {
    if (!priceBook) return '-';

    switch (priceBook.pricingMethod) {
      case 'FIXED':
        return entry.fixedPrice !== null ? formatCurrency(entry.fixedPrice) : '-';
      case 'RACK_PLUS':
        return entry.rackPlusAmount !== null ? `Rack + $${entry.rackPlusAmount.toFixed(4)}` : '-';
      case 'MARGIN_PERCENT':
        return entry.marginPercent !== null ? `${entry.marginPercent.toFixed(1)}% margin` : '-';
      case 'COST_PLUS':
        return entry.costPlusAmount !== null ? `Cost + $${entry.costPlusAmount.toFixed(4)}` : '-';
      default:
        return '-';
    }
  };

  const calculateEffectivePrice = (entry: PriceBookEntry) => {
    if (!priceBook) return null;
    const cost = Number(entry.product.costPerUnit) || 0;

    switch (priceBook.pricingMethod) {
      case 'FIXED':
        return entry.fixedPrice;
      case 'RACK_PLUS':
        return cost + (entry.rackPlusAmount || 0);
      case 'MARGIN_PERCENT':
        return cost * (1 + (entry.marginPercent || 0) / 100);
      case 'COST_PLUS':
        return cost + (entry.costPlusAmount || 0);
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-text-tertiary font-mono animate-pulse">
          &gt; Loading price book...
        </div>
      </div>
    );
  }

  if (error || !priceBook) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="text-error-red font-mono mb-4">{error || 'Price book not found'}</div>
        <button
          onClick={() => navigate('/pricing/pricebooks')}
          className="text-amber hover:text-amber/80 font-mono"
        >
          ← Back to Price Books
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-4 mb-2">
                <h1
                  className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                  style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
                >
                  {priceBook.name}
                </h1>
                {priceBook.isDefault && (
                  <span className="px-3 py-1 text-sm font-mono bg-amber/20 text-amber border border-amber">
                    DEFAULT
                  </span>
                )}
                <span
                  className={`px-3 py-1 text-sm font-mono border ${
                    priceBook.isActive
                      ? 'bg-success-green/20 text-success-green border-success-green'
                      : 'bg-gray-500/20 text-gray-400 border-gray-500'
                  }`}
                >
                  {priceBook.isActive ? 'ACTIVE' : 'INACTIVE'}
                </span>
              </div>
              <p className="text-text-secondary text-sm font-mono">
                {PRICING_METHOD_LABELS[priceBook.pricingMethod]} • {priceBook._count.prices}{' '}
                products • {priceBook._count.customers} customers
              </p>
            </div>
            <button
              onClick={() => navigate('/pricing/pricebooks')}
              className="text-text-secondary hover:text-amber font-mono text-sm uppercase tracking-wider"
            >
              ← Back to Price Books
            </button>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        <Link
          to={`/pricing/pricebooks/${priceBook.id}/edit`}
          className="bg-amber hover:bg-amber/90 text-background font-mono py-2 px-4 transition-all uppercase tracking-wider text-sm flex items-center space-x-2"
        >
          <span>✏️</span>
          <span>Edit Price Book</span>
        </Link>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Products" value={priceBook._count.prices.toString()} />
        <StatCard label="Customers" value={priceBook._count.customers.toString()} />
        <StatCard label="Effective" value={formatDate(priceBook.effectiveDate)} />
        <StatCard label="Expires" value={formatDate(priceBook.expirationDate)} />
      </div>

      {/* Description */}
      {priceBook.description && (
        <GeometricPanel>
          <h3 className="text-lg font-display text-text-primary uppercase tracking-wide mb-3 pb-2 border-b border-amber">
            Description
          </h3>
          <p className="text-text-secondary font-mono text-sm">{priceBook.description}</p>
        </GeometricPanel>
      )}

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-panel-border">
        <button
          onClick={() => setActiveTab('prices')}
          className={`px-6 py-3 font-mono text-sm uppercase tracking-wider transition-all ${
            activeTab === 'prices'
              ? 'text-amber border-b-2 border-amber bg-amber/5'
              : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          Product Prices ({priceBook.prices.length})
        </button>
        <button
          onClick={() => setActiveTab('customers')}
          className={`px-6 py-3 font-mono text-sm uppercase tracking-wider transition-all ${
            activeTab === 'customers'
              ? 'text-amber border-b-2 border-amber bg-amber/5'
              : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          Assigned Customers ({priceBook.customers.length})
        </button>
      </div>

      {/* Prices Tab */}
      {activeTab === 'prices' && (
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border overflow-hidden">
            {priceBook.prices.length === 0 ? (
              <div className="text-center py-8 text-text-tertiary font-mono">
                No product prices configured
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-background border-b border-panel-border">
                    <tr>
                      <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Product
                      </th>
                      <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Cost
                      </th>
                      <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        List Price
                      </th>
                      <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Book Price
                      </th>
                      <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Effective
                      </th>
                      <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Min/Max
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {priceBook.prices.map((entry) => {
                      const effectivePrice = calculateEffectivePrice(entry);
                      return (
                        <tr
                          key={entry.id}
                          className="border-b border-panel-border hover:bg-amber/5"
                        >
                          <td className="px-6 py-4">
                            <div className="text-text-primary font-mono">{entry.product.name}</div>
                            <div className="text-text-tertiary text-xs font-mono">
                              {entry.product.sku}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right text-text-secondary font-mono">
                            {formatCurrency(Number(entry.product.costPerUnit))}
                          </td>
                          <td className="px-6 py-4 text-right text-text-secondary font-mono">
                            {formatCurrency(Number(entry.product.pricePerUnit))}
                          </td>
                          <td className="px-6 py-4 text-right text-steel font-mono">
                            {getPriceDisplay(entry)}
                          </td>
                          <td className="px-6 py-4 text-right text-amber font-mono font-semibold">
                            {effectivePrice ? formatCurrency(effectivePrice) : '-'}
                          </td>
                          <td className="px-6 py-4 text-right text-text-tertiary font-mono text-sm">
                            {entry.minPrice || entry.maxPrice ? (
                              <span>
                                {entry.minPrice ? `$${entry.minPrice.toFixed(2)}` : '-'} /{' '}
                                {entry.maxPrice ? `$${entry.maxPrice.toFixed(2)}` : '-'}
                              </span>
                            ) : (
                              '-'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Customers Tab */}
      {activeTab === 'customers' && (
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-6">
            {priceBook.customers.length === 0 ? (
              <div className="text-center py-8 text-text-tertiary font-mono">
                No customers assigned to this price book
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {priceBook.customers.map((customer) => (
                  <Link
                    key={customer.id}
                    to={`/customers/${customer.id}`}
                    className="bg-background p-4 border border-panel-border hover:border-amber/50 transition-colors"
                  >
                    <div className="text-text-primary font-mono font-semibold">
                      {customer.name}
                    </div>
                    <div className="text-text-tertiary font-mono text-sm">
                      {customer.customerNumber}
                    </div>
                    <div className="flex items-center space-x-2 mt-2">
                      <span className="text-text-tertiary text-xs font-mono">{customer.type}</span>
                      <span
                        className={`text-xs font-mono ${
                          customer.status === 'ACTIVE' ? 'text-success-green' : 'text-gray-400'
                        }`}
                      >
                        {customer.status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Activity */}
      <GeometricPanel>
        <h3 className="text-lg font-display text-text-primary uppercase tracking-wide mb-3 pb-2 border-b border-amber">
          Activity
        </h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-text-tertiary font-mono">Created</span>
            <span className="text-text-secondary font-mono">
              {formatDateTime(priceBook.createdAt)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-text-tertiary font-mono">Updated</span>
            <span className="text-text-secondary font-mono">
              {formatDateTime(priceBook.updatedAt)}
            </span>
          </div>
        </div>
      </GeometricPanel>
    </div>
  );
};

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="relative">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="bg-panel-dark border border-panel-border p-4">
        <div className="text-text-tertiary text-xs uppercase tracking-widest font-mono mb-2">
          {label}
        </div>
        <div className="text-lg font-mono font-bold text-amber">{value}</div>
      </div>
    </div>
  );
}

export default PriceBookDetailPage;
